import type { ReactNode } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useLogout } from '../api/queries'
import { cn } from '../lib/cn'
import { useSession } from '../session'
import { ThemeToggle } from './ThemeToggle'
import { Button } from './ui'

/**
 * Каркас приложения. Отдельного «режима администратора» нет: у кого есть право,
 * тот видит лишний пункт в меню — так решено в сценариях.
 */
export function AppLayout() {
  const { user } = useSession()
  const navigate = useNavigate()
  const logout = useLogout()

  const isBlocked = user.status === 'blocked'

  return (
    <div className="flex min-h-full flex-col bg-surface-2">
      {/* Ссылка в обход меню: с клавиатуры до расписания иначе идти через все вкладки. */}
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow-pop"
      >
        Перейти к содержимому
      </a>

      <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
        {/*
          Шапка переносится, а не уезжает за экран.

          Раньше строка была одна и без переноса: на 390 точках меню, тумблер темы
          и «Выйти» не помещались, правая группа начиналась за правым краем, и вместе
          с ней уезжала вся страница (document.scrollWidth 515 при окне 390). С телефона
          нельзя было ни выйти, ни переключить тему — то есть половина шапки просто
          не существовала.

          Теперь на узком экране меню уходит на вторую строку целиком (order-last +
          w-full), а на первой остаются логотип и правая группа. Пункты меню, если их
          всё же больше, чем помещается, прокручиваются внутри своей строки — страница
          при этом стоит на месте. С sm вёрстка прежняя: одна строка высотой 56.
        */}
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0">
          <NavLink to="/" className="mr-2 flex items-center gap-2 font-semibold tracking-tight">
            <span aria-hidden className="grid h-7 w-7 place-items-center rounded-md bg-accent text-accent-fg">
              П
            </span>
            {/* На узком экране название прячется визуально, но не от скринридера:
                иначе у главной ссылки остаётся имя «П». */}
            <span className="sr-only sm:not-sr-only sm:inline">Переговорки</span>
          </NavLink>

          <nav
            aria-label="Основные разделы"
            /*
              min-w-0 и прокрутка на ВСЕХ ширинах, а не только на узких.

              Раньше с sm меню переставало прокручиваться (sm:overflow-visible)
              и не могло сжаться: по умолчанию элемент flex не уменьшается
              меньше своего содержимого. Правая группа при этом shrink-0,
              строка sm:flex-nowrap. В итоге на промежуточной ширине — около
              740 точек, когда у администратора четыре вкладки, — строка
              не помещалась и толкала всю страницу вправо на несколько точек:
              появлялась горизонтальная прокрутка всего документа.

              Ловушка в том, что зависит это от числа вкладок и длины их
              названий: у обычного сотрудника три вкладки и всё помещается,
              у администратора четыре — и уже нет. Поэтому не «подобрать
              ширину», а снять саму возможность: меню сжимается и прокручивается
              внутри себя, страница остаётся на месте при любом наборе пунктов.
            */
            className="order-last w-full min-w-0 overflow-x-auto sm:order-none sm:w-auto"
          >
            <div className="flex w-max items-center gap-1">
              <Tab to="/" end>
                День
              </Tab>
              <Tab to="/search">Поиск</Tab>
              <Tab to="/meetings">Встречи</Tab>
              {user.is_admin && <Tab to="/admin/rooms">Управление</Tab>}
            </div>
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ThemeToggle />
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight">
                {user.first_name} {user.last_name}
              </p>
              <p className="tnum text-xs leading-tight text-muted">{user.email}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              loading={logout.isPending}
              onClick={() => {
                logout.mutate(undefined, { onSuccess: () => navigate('/login', { replace: true }) })
              }}
            >
              Выйти
            </Button>
          </div>
        </div>

        {isBlocked && (
          <div className="border-t border-state-busy/30 bg-state-busy/10 px-4 py-2 text-center text-sm text-fg">
            Бронирование вам сейчас недоступно — расписание видно, создавать встречи нельзя.{' '}
            <NavLink to="/blocked" className="font-medium text-accent underline-offset-2 hover:underline">
              Подробнее
            </NavLink>
          </div>
        )}
      </header>

      <main id="content" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="border-t border-line px-4 py-4 text-center text-xs text-muted">
        Рабочие часы и горизонт бронирования задаёт администратор в разделе «Правила».
      </footer>
    </div>
  )
}

function Tab({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'rounded-lg px-3 py-1.5 text-sm font-medium transition',
          isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:text-fg',
        )
      }
    >
      {children}
    </NavLink>
  )
}
